import clsx from "clsx";
import { type ComponentProps, useState } from "react";
import { EffectFade, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { COLORS_VALUES } from "@shared/constants";
import type { ColorIconType } from "@shared/content-types/types/common";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Image } from "@shared/ui/image";
import { SwiperNavButtons } from "@shared/ui/slider-nav-buttons";
import { Body } from "@shared/ui/typography/body";
import {
  formatDate,
  isDifferentDate,
} from "@shared/utils/string";

import type { EventType } from "@/_pages/event/model";

import s from "./events-slider.module.scss";

export type EventsSliderProps = ComponentProps<"div"> & {
  className?: string;
  events: EventType[];
  pageIcon: ColorIconType;
};

export const EventsSlider = (props: EventsSliderProps) => {
  const { className, events, pageIcon } = props;

  const [currentIndex, setCurrentIndex] = useState(0);

  return (
    <div className={clsx(s.root, className)}>
      <Swiper
        className={s.slider}
        modules={[EffectFade, Pagination]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        slidesPerView={1}
        onSlideChange={({ realIndex }) => setCurrentIndex(realIndex)}
        loop
        pagination={{
          el: `.${s.pagination}`,
          type: "bullets",
          clickable: true,
          bulletClass: `${s.bullet}`,
          bulletActiveClass: `${s.bulletActive}`,
        }}
      >
        {(events ?? []).map((event, eventIdx) =>
          event ? (
            <SwiperSlide
              key={event?.documentId ?? event?.slug ?? eventIdx}
              className={s.slide}
            >
              <div
                className={s.slideContent}
                style={{
                  backgroundColor: event?.mainColor
                    ? COLORS_VALUES[
                        event.mainColor as keyof typeof COLORS_VALUES
                      ]
                    : undefined,
                }}
              >
                <Body
                  size="uppercase-small"
                  weight="semiBold"
                  className={clsx(s.subtitle, s.subtitleM)}
                >
                  Ближайшие события
                </Body>
                {event?.image?.url && (
                  <Image
                    src={event.image.url}
                    alt={event.image?.alternativeText ?? "event image"}
                    className={s.image}
                  />
                )}
                <div className={s.textContent}>
                  <Body
                    size="uppercase-small"
                    weight="semiBold"
                    className={s.subtitle}
                  >
                    Ближайшие события
                  </Body>
                  <div className={s.title}>
                    {event.date && (
                      <p className={s.location}>
                        {event.date && (
                          <span>
                            {formatDate(
                              event.date,
                              isDifferentDate(
                                event.date,
                                event?.dateEnd!,
                                "month",
                              ),
                              isDifferentDate(
                                event.date,
                                event?.dateEnd!,
                                "year",
                              ),
                            )}
                          </span>
                        )}
                        {event.dateEnd && (
                          <span>
                            {" "}
                            –{" "}
                            {formatDate(
                              event.dateEnd,
                              true,
                              isDifferentDate(
                                event.date,
                                event.dateEnd,
                                "year",
                              ),
                            )}
                          </span>
                        )}
                        {", "}
                      </p>
                    )}
                    <p className={s.location}>{event.location}</p>
                    <p>{event.title}</p>
                  </div>
                  <ButtonWithIcon
                    href={event?.slug ? `/events/${event.slug}` : undefined}
                    iconFillColor={pageIcon?.fillColor}
                    iconStrokeColor={pageIcon?.strokeColor}
                    iconName={pageIcon?.icon?.name ?? "plus"}
                    iconSvgCode={pageIcon?.uicon?.svg_code}
                    className={s.button}
                  >
                    подробнее
                  </ButtonWithIcon>
                </div>
              </div>
            </SwiperSlide>
          ) : null,
        )}
        {(events?.length ?? 0) > 1 && (
          <>
            <SwiperNavButtons
              className={s.navButtons}
              variant="light"
              totalSlides={events.length}
              currentIndex={currentIndex}
            />
            <div className={s.pagination}></div>
          </>
        )}
      </Swiper>
    </div>
  );
};

EventsSlider.displayName = "EventsSlider";
